import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { IgfdPaymentPlan, Restaurant } from "api-types";
import { onRestaurantSelected } from "dashboard/src/monitoring";

import type {
  ReduxState,
  RestaurantDraft,
  ThunkExtraArgument,
} from "@dashboard/types";

import { type ErrorReducerAction } from "../reducers/errorReducer";
import { type RestaurantReducerAction } from "../reducers/restaurantReducer";
import { addError } from "./addErrorAction";

export type RestaurantReducerDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction | ErrorReducerAction
>;

// deprecated
type RestaurantReducerThunkAction = ThunkAction<
  void,
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction
>;

type RestaurantReducerThunkPromiseAction = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction | ErrorReducerAction
>;

export const fetchAccessibleRestaurants: (
  userId: string, // firebase auth user id
  onError: (error: Error) => void
) => RestaurantReducerThunkAction = (
  userId: string,
  onError: (error: Error) => void
) => {
  return (
    dispatch: RestaurantReducerDispatch,
    getState: () => ReduxState,
    { restaurantApi }: ThunkExtraArgument
  ) => {
    // This is present when the page is refreshed, or during load from persisted state.
    const previouslySelectedRestUuid =
      getState().restaurant.selectedRestaurant?.uuid;
    const isAdminUser = getState().dashboardApp.userIsAdmin;

    restaurantApi
      .fetchAccessibleRestaurants(userId)
      .then((restaurantAndPlans) => {
        dispatch({ type: "FETCHED_RESTAURANTS", payload: restaurantAndPlans });

        // We want to dispatch the restaurant selected event to ensure all other parts of the app
        // that depend on this action being dispatched, works correctly.
        // However the result of the fetchAccessibleRestaurants won't contain the previously selected
        // restaurant for admin users. Hence for them we fetch this data again separately.
        if (isAdminUser && previouslySelectedRestUuid) {
          // If a admin user already had selected a restaurant, keep it selected.
          // The previous restaurant may not exist in the list of accessible restaurants,
          // so we refetch it separately.
          restaurantApi
            .fetchRestaurantAndPaymentPlan(previouslySelectedRestUuid)
            .then((restaurantAndPlan) => {
              dispatch({
                type: "UPDATE_PAYMENT_PLAN",
                payload: {
                  restaurantUuid: previouslySelectedRestUuid,
                  paymentPlan: restaurantAndPlan.paymentPlan,
                },
              });
              dispatch({
                type: "RESTAURANT_SELECTED",
                payload: restaurantAndPlan.restaurant,
              });
            })
            .catch(onError);
        } else {
          const previouslySelectedRestaurant = restaurantAndPlans.find(
            (r) => r.restaurant.uuid === previouslySelectedRestUuid
          )?.restaurant;

          // If no restaurant was previously selected (first load without local state) or
          // the user no longer has access to the previously selected restaurant, select
          // the first restaurant by default if there is one.
          const restaurantToSelect =
            previouslySelectedRestaurant ||
            restaurantAndPlans[0]?.restaurant ||
            null;

          dispatch({
            type: "RESTAURANT_SELECTED",
            payload: restaurantToSelect,
          });

          // no need to dispatch the payment plan update action here, as the payment plan
          // is already updated with the FETCHED_RESTAURANTS action.
        }
      })
      .catch(onError);
  };
};

export const restaurantSelectedAction: (
  restaurant: Restaurant
) => ThunkAction<
  void,
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction
> = (restaurant: Restaurant) => {
  return (dispatch: RestaurantReducerDispatch) => {
    onRestaurantSelected(restaurant); // track in analytics (set restaurant uuid in datadog)

    // This updates several parts of the app with the correct restaurant.
    // Payment plays for all restaurants accessible to the user has already been fetched
    // and is present in the redux store.
    dispatch({ type: "RESTAURANT_SELECTED", payload: restaurant });
  };
};

export const restaurantCreatedAction: (
  restaurant: Restaurant
) => RestaurantReducerThunkAction = (restaurant: Restaurant) => {
  return (dispatch: RestaurantReducerDispatch) => {
    dispatch({ type: "RESTAURANT_CREATED", payload: restaurant });
    // select the newly created restaurant
    dispatch(restaurantSelectedAction(restaurant));
  };
};

/**
 * A restaurant selected by an admin user, from the Admin Mode page.
 *
 * This involves setting the payment plan as well.
 */
export const restaurantSelectedByAdminAction: (
  restaurant: Restaurant,
  paymentPlan: IgfdPaymentPlan
) => RestaurantReducerThunkAction = (
  restaurant: Restaurant,
  paymentPlan: IgfdPaymentPlan
) => {
  return (dispatch: RestaurantReducerDispatch) => {
    onRestaurantSelected(restaurant); // track in analytics
    // update the payment plan
    dispatch({
      type: "UPDATE_PAYMENT_PLAN",
      payload: { restaurantUuid: restaurant.uuid, paymentPlan },
    });
    dispatch({ type: "RESTAURANT_SELECTED", payload: restaurant });
  };
};

// Updates the restaurant entity in firestore
export const updateRestaurant: (
  restaurant: Partial<RestaurantDraft>
) => RestaurantReducerThunkPromiseAction = (
  restaurant: Partial<RestaurantDraft>
) => {
  return (
    dispatch: RestaurantReducerDispatch,
    getState: () => ReduxState,
    { restaurantApi }: ThunkExtraArgument
  ) => {
    const { selectedRestaurant } = getState().restaurant;
    if (!selectedRestaurant) {
      return Promise.resolve();
    }

    return restaurantApi
      .updateRestaurantDetails(selectedRestaurant.uuid, restaurant)
      .then(() => {
        dispatch({
          type: "RESTAURANT_DETAILS_UPDATED",
          payload: restaurant,
        });
      })
      .catch(addError(dispatch));
  };
};
